import {
  borderWidth,
  duration,
  fontSize,
  fontWeight,
  letterSpacing,
  lineHeight,
  radius,
  space,
} from './scales'
import type { Theme } from './theme'

const PREFIX = '--st'

const kebab = (key: string): string =>
  key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

/**
 * 테마를 CSS 커스텀 프로퍼티 선언 목록으로 바꾼다 (웹·데스크톱 전용).
 * 스케일은 테마와 무관하므로 :root에 한 번만 넣으면 된다 → staticVariables 사용.
 */
export function themeVariables(theme: Theme): string {
  return Object.entries(theme.colors)
    .map(([key, value]) => `${PREFIX}-color-${kebab(key)}: ${value};`)
    .join('\n')
}

/** 테마와 무관한 스케일 변수 */
export function staticVariables(): string {
  const lines: string[] = []

  for (const [key, value] of Object.entries(space)) {
    lines.push(`${PREFIX}-space-${key}: ${value}px;`)
  }
  for (const [key, value] of Object.entries(radius)) {
    lines.push(`${PREFIX}-radius-${key}: ${value}px;`)
  }
  for (const [key, value] of Object.entries(borderWidth)) {
    lines.push(`${PREFIX}-border-${key}: ${value}px;`)
  }
  for (const [key, value] of Object.entries(fontSize)) {
    lines.push(`${PREFIX}-font-size-${key}: ${value}px;`)
  }
  for (const [key, value] of Object.entries(lineHeight)) {
    lines.push(`${PREFIX}-line-height-${key}: ${value}px;`)
  }
  for (const [key, value] of Object.entries(fontWeight)) {
    lines.push(`${PREFIX}-font-weight-${key}: ${value};`)
  }
  for (const [key, value] of Object.entries(letterSpacing)) {
    lines.push(`${PREFIX}-letter-spacing-${key}: ${value}px;`)
  }
  // 폰트 패밀리는 스케일이 아니라 값 하나지만, CSS 쪽에서 var() 로 쓰려면 여기서 나가야 한다
  lines.push(`${PREFIX}-font-family: ${fontFamily};`)
  lines.push(`${PREFIX}-mono-font-family: ${monoFontFamily};`)
  for (const [key, value] of Object.entries(duration)) {
    lines.push(`${PREFIX}-duration-${key}: ${value}ms;`)
  }

  return lines.join('\n')
}

/** CSS 변수 참조 헬퍼 — `color: var(--st-color-text)` 를 타입 안전하게 쓰기 위한 것 */
export const cssVar = {
  color: (key: keyof Theme['colors']) => `var(${PREFIX}-color-${kebab(key)})`,
  space: (key: keyof typeof space) => `var(${PREFIX}-space-${key})`,
  radius: (key: keyof typeof radius) => `var(${PREFIX}-radius-${key})`,
  fontSize: (key: keyof typeof fontSize) => `var(${PREFIX}-font-size-${key})`,
  lineHeight: (key: keyof typeof lineHeight) => `var(${PREFIX}-line-height-${key})`,
  fontWeight: (key: keyof typeof fontWeight) => `var(${PREFIX}-font-weight-${key})`,
  duration: (key: keyof typeof duration) => `var(${PREFIX}-duration-${key})`,
}

/** 웹 폰트 스택 — 시스템 폰트만 사용해 네트워크 요청 0 */
export const fontFamily =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", "Apple SD Gothic Neo", "Malgun Gothic", sans-serif'

export const monoFontFamily =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace'
